import { useEffect, useRef, useState } from 'react';
import { getServiceRequest } from '@/services/api/location';
import { processAPIResponseNow } from '@/utils/request';

type CalendarOverbooking = {
  unlimited_booking?: boolean | null;
  additional_capacity?: number | null;
};

/**
 * Branch detail does not include the CalKit calendar. The overbooking pair
 * arrives on GET /branch-services/:id as `calendar.unlimited_booking` and
 * `calendar.additional_capacity`.
 */
export function hasCalendarOverbooking(calendar?: CalendarOverbooking | null) {
  if (!calendar) return false;
  return 'unlimited_booking' in calendar || 'additional_capacity' in calendar;
}

export function useHydrateBranchServiceCalendar(
  serviceId: string | undefined,
  calendar: CalendarOverbooking | null | undefined,
  onHydrate: (calendar: Record<string, any>) => void,
) {
  const onHydrateRef = useRef(onHydrate);
  onHydrateRef.current = onHydrate;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!serviceId || hasCalendarOverbooking(calendar)) return;

    let cancelled = false;
    setFailed(false);

    getServiceRequest({ params: { serviceId } }).then((res) => {
      processAPIResponseNow(res, {
        onSuccess(body) {
          if (cancelled) return;
          const next = body?.data?.calendar;
          if (!next || !hasCalendarOverbooking(next)) {
            setFailed(true);
            return;
          }
          onHydrateRef.current(next);
        },
        onError() {
          if (!cancelled) setFailed(true);
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [serviceId, calendar?.unlimited_booking, calendar?.additional_capacity]);

  return { failed };
}
